import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tablet, AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ManagePins } from "@/components/tablet/ManagePins";

// Manager setup: choose a kitchen, register THIS device, then hand over (manager is signed out).
export function TabletSetup({ onRegistered, notice }) {
  const { user, has, logout } = useAuth();
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [name, setName] = useState("Kitchen tablet");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("register");
  const allowed = !!user && has("manage_people");
  useEffect(() => {
    if (allowed) api.get("/venues").then((r) => { const s = (r.data || []).filter((v) => v.active !== false); setSites(s); if (s.length === 1) setSiteId(s[0].venue_id); }).catch(() => {});
  }, [allowed]);

  const register = async () => {
    setBusy(true);
    let token;
    try {
      const r = await api.post("/tablet/devices", { site_id: siteId, display_name: name.trim() || "Kitchen tablet", platform: "web" });
      token = r.data.device_token;
    } catch (e) {
      toast.error(`${e?.normalized?.message || e?.response?.data?.detail || "Could not register this tablet"}. You're still signed in.`);
      setBusy(false);
      return; // manager session kept
    }
    localStorage.setItem("cc_tablet_device", token);
    await logout(); // server session + cookie + stored/native token removed; device credential kept
    toast.success("Tablet ready. You've been signed out.");
    onRegistered();
    navigate("/tablet", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-start sm:items-center justify-center p-4 sm:p-6" data-testid="tablet-register">
      <div className="w-full max-w-2xl bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-700">
        <div className="flex items-center gap-3 mb-2"><Tablet className="text-brand" /><h1 className="text-2xl font-bold">Set up Kitchen Tablet</h1></div>
        {notice && <div className="flex items-center gap-2 text-amber-300 text-sm my-3" data-testid="tablet-setup-notice"><AlertTriangle size={16} />{notice}</div>}
        {!user && (
          <div className="space-y-4" data-testid="tablet-signin-required">
            <p className="text-slate-300">This device isn't set up as a kitchen tablet. A manager needs to sign in to register it to a kitchen.</p>
            <button onClick={() => navigate("/login?next=/tablet")} data-testid="tablet-manager-signin" className="w-full py-4 rounded-2xl bg-brand text-white font-semibold">Manager sign in</button>
          </div>
        )}
        {user && !allowed && <p className="text-amber-300 text-sm" data-testid="tablet-not-allowed">Only managers who manage people can set up a kitchen tablet. Ask your manager.</p>}
        {allowed && (
          <>
            <div className="flex gap-2 my-4">
              {[["register", "Register device"], ["pins", "Staff PINs"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tablet-setup-tab-${k}`} className={`px-4 py-2 rounded-xl text-sm ${tab === k ? "bg-brand text-white" : "bg-slate-800 text-slate-300"}`}>{l}</button>))}
            </div>
            {tab === "pins" ? <ManagePins client={api} /> : (
              <div className="space-y-4">
                <div className="rounded-2xl bg-slate-800/60 border border-slate-700 p-4 text-sm text-slate-300 space-y-1" data-testid="tablet-handoff-explainer">
                  <p className="font-semibold text-white">What happens next</p>
                  <p>1. This device is registered to the kitchen you choose.</p>
                  <p>2. <strong>You are signed out</strong> of your personal account on this device, and your dashboard can't be reopened without signing in again.</p>
                  <p>3. Staff then tap their name and enter their kitchen PIN to open their own kitchen dashboard.</p>
                  <p>Before handing over, check staff PINs in the "Staff PINs" tab.</p>
                </div>
                <label className="block text-sm">Kitchen
                  <select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="tablet-site-select" className="mt-1 w-full p-3 rounded-xl bg-slate-800 text-white">
                    <option value="">Choose a kitchen…</option>
                    {sites.map((s) => <option key={s.venue_id} value={s.venue_id}>{s.name}</option>)}
                  </select></label>
                <label className="block text-sm">Tablet name
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="tablet-name-input" className="mt-1 w-full p-3 rounded-xl bg-slate-800 text-white" /></label>
                <button disabled={!siteId || busy} onClick={register} data-testid="tablet-register-submit" className="w-full py-4 rounded-2xl bg-brand text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy && <Loader2 size={18} className="animate-spin" />}Register and enter tablet mode</button>
              </div>
            )}
          </>
        )}
        {user && <button onClick={() => navigate("/dashboard")} data-testid="tablet-setup-back" className="mt-6 text-slate-400 text-sm flex items-center gap-2"><ArrowLeft size={14} /> Back to CaterCTRL</button>}
      </div>
    </div>
  );
}
